import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PatternsSummary } from './PatternsSummary';
import { FrequencyCard } from './FrequencyCard';
import { PatternsLists } from './PatternsLists';
import { PatternsStoryDetails } from './PatternsStoryDetails';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PatternsPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/pattern-analysis`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed (${res.status})`);
        const json = await res.json();
        if (!aborted) setData(json);
      } catch (e) {
        if (!aborted) setError(e.message);
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    run();
    return () => {
      aborted = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-6">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading patterns…
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600 text-sm">{error}</p>;
  }

  if (!data || (data.total_stories || 0) === 0) {
    return <p className="text-gray-500 text-sm">No expanded stories yet to analyse.</p>;
  }

  return (
    <div data-testid="patterns-panel">
      <PatternsSummary
        totalStories={data.total_stories}
        archetypes={data.archetypes}
        countries={data.countries}
      />

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <FrequencyCard title="Story Archetypes" data={data.archetypes} />
        <FrequencyCard title="Themes" data={data.themes} />
        <FrequencyCard title="Countries" data={data.countries} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <FrequencyCard title="Evidence Categories" data={data.evidence_categories} />
        <FrequencyCard title="Top Tags" data={data.top_tags} />
      </div>

      <PatternsLists patterns={data.recurring_patterns} />

      <PatternsStoryDetails barriers={data.barriers} postureShifts={data.posture_shifts} />
    </div>
  );
};

export default PatternsPanel;
